import { campusSlug } from './campus';
import type { FinderData, LocationGroup } from './types';

/**
 * Resolve whatever a link or embed says about a campus to a Congregation_ID,
 * and back again for the URL the widget writes.
 *
 * Accepted forms, in order:
 *   - a numeric Congregation_ID ("15")
 *   - MP's real `Congregations.Campus_Slug` via /api/locations ("troy",
 *     "troy-español")
 *   - the campus name or display name ("Troy", "Español", "Troy Espanol Campus")
 *   - the finder proc's own label ("Troy Campus") when locations are unavailable
 *
 * Every candidate is folded through `campusSlug` (lowercase, diacritics
 * stripped, trailing "campus" dropped) so `troy-espanol`, `Troy Español` and
 * `troy-espa%C3%B1ol` all land on the same campus.
 */
export interface CampusIndex {
  resolve(value: string): number | null;
  /** The shareable slug for a campus, MP's when known, else derived from its name. */
  slugFor(id: number): string | null;
}

export function buildCampusIndex(
  locations: LocationGroup[],
  campuses: FinderData['options']['campuses']
): CampusIndex {
  const ids = new Set(campuses.map((c) => c.id));
  const byKey = new Map<string, number>();
  const slugById = new Map<number, string>();

  const learn = (id: number, ...names: (string | null | undefined)[]) => {
    for (const name of names) {
      if (!name) continue;
      const key = campusSlug(name);
      if (key && !byKey.has(key)) byKey.set(key, id);
    }
  };

  // Real slugs first so they win over derived ones.
  for (const group of locations) {
    for (const cong of group.congregations) {
      if (cong.campus_slug) {
        learn(cong.id, cong.campus_slug);
        slugById.set(cong.id, cong.campus_slug);
      }
    }
  }
  for (const group of locations) {
    for (const cong of group.congregations) learn(cong.id, cong.name, cong.display_name);
  }
  for (const c of campuses) {
    learn(c.id, c.label);
    if (!slugById.has(c.id)) slugById.set(c.id, campusSlug(c.label));
  }

  return {
    resolve(value) {
      const v = value.trim();
      if (!v) return null;
      if (/^\d+$/.test(v)) {
        const id = Number(v);
        return ids.has(id) ? id : null;
      }
      const id = byKey.get(campusSlug(v));
      return id !== undefined && ids.has(id) ? id : null;
    },
    slugFor(id) {
      return slugById.get(id) ?? null;
    },
  };
}
